<template>
    <div class="question-text" :class="{ 'exam-mode': examMode }">
        <h3>Question #{{ questionId }}:</h3>
        <div v-if="sharedContext" v-html="formatText(sharedContext)" class="question-content"></div>
        <div v-html="formatText(questionText)" class="question-content"></div>
        <div v-if="imageUrls?.length">
            <img
                v-for="(url, i) in imageUrls"
                :key="i"
                :src="url"
                class="question-image"
            />
        </div>
    </div>
</template>

<script setup>
import { onMounted, onUpdated, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useMarkdownRenderer } from '@/composables/useMarkdownRenderer';

defineProps({
    sharedContext: String,
    questionText: String,
    imageUrls: Array,
    questionId: Number
});

const authStore = useAuthStore();
const { formatText, renderMathAsync } = useMarkdownRenderer();

// Exam mode requires Tier 2+ subscription
const examMode = computed(() => {
    const hasTier = authStore.hasTierAccess('TIER 2');
    const isEnabled = authStore.settings?.appearance?.exam_mode ?? false;
    return hasTier && isEnabled;
});

const renderMath = async () => {
    const questionSection = document.querySelector('.question-text');
    await renderMathAsync(questionSection);
};

onMounted(renderMath);
onUpdated(renderMath);
</script>

<style scoped>
.question-text {
    margin-bottom: 20px;
}

.question-text h3 {
    color: var(--text-secondary);
    font-size: 18px;
}

.question-content {
    font-size: 16px;
    line-height: 1.8;
    color: var(--text-primary);
    white-space: pre-wrap;
    opacity: 0.9;
    text-align: justify;
}

/* Legacy support for any remaining p tags */
.question-text p {
    font-size: 16px;
    line-height: 1.6;
    color: var(--text-primary);
    white-space: pre-wrap;
    opacity: 0.9;
    text-align: justify;
}

.question-image {
    max-width: 100%;
    height: auto;
    margin: 10px 0;
    max-height: 30vh;
}

/* Tables from v-html content */
.question-text :deep(table) {
    width: 60%;
    border-collapse: collapse;
    margin: 16px auto;
    font-size: 14px;
}

.question-text :deep(th),
.question-text :deep(td) {
    border: 1px solid rgba(var(--accent-rgb), 0.3);
    padding: 10px 14px;
    text-align: center;
}

.question-text :deep(th) {
    background: rgba(var(--accent-rgb), 0.15);
    color: var(--text-secondary);
    font-weight: 600;
}

.question-text :deep(tr:nth-child(even)) {
    background: rgba(var(--accent-rgb), 0.05);
}

/* Lists from v-html content */
.question-text :deep(ul),
.question-text :deep(ol) {
    margin: 12px 0;
    padding-left: 24px;
    text-align: left;
}

.question-text :deep(li) {
    margin: 2px 0;
    line-height: 1.4;
    list-style-position: outside;
    padding-left: 0;
}

/* KaTeX math expressions - prevent line wrapping */
.question-text :deep(.katex) {
    white-space: nowrap;
}

.question-text :deep(.katex-display) {
    white-space: nowrap;
    overflow-x: auto;
}

/* Markdown support */
.question-text :deep(h1),
.question-text :deep(h2),
.question-text :deep(h3),
.question-text :deep(h4),
.question-text :deep(h5),
.question-text :deep(h6) {
    margin: 16px 0 12px 0;
    color: var(--accent);
    font-weight: 600;
    text-align: left;
}

.question-text :deep(h1) { font-size: 24px; }
.question-text :deep(h2) { font-size: 20px; }
.question-text :deep(h3) { font-size: 18px; }
.question-text :deep(h4) { font-size: 16px; }
.question-text :deep(h5) { font-size: 15px; }
.question-text :deep(h6) { font-size: 14px; }

.question-text :deep(strong) {
    font-weight: 600;
    color: var(--text-primary);
}

.question-text :deep(em) {
    font-style: italic;
}

.question-text :deep(code) {
    background: rgba(var(--accent-rgb), 0.1);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
}

.question-text :deep(pre) {
    background: rgba(var(--accent-rgb), 0.05);
    padding: 16px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 12px 0;
    text-align: left;
}

.question-text :deep(pre code) {
    background: none;
    padding: 0;
}

.question-text :deep(a) {
    color: var(--accent);
    text-decoration: underline;
}

.question-text :deep(a:hover) {
    opacity: 0.8;
}

.question-text :deep(blockquote) {
    border-left: 4px solid var(--accent);
    padding-left: 16px;
    margin: 16px 0;
    color: var(--text-secondary);
    font-style: italic;
    text-align: left;
}

.question-text :deep(hr) {
    border: none;
    border-top: 1px solid rgba(var(--accent-rgb), 0.3);
    margin: 16px 0;
}

/* Exam mode - HSC serif font */
.question-text.exam-mode,
.question-text.exam-mode .question-content,
.question-text.exam-mode p,
.question-text.exam-mode :deep(li),
.question-text.exam-mode :deep(td),
.question-text.exam-mode :deep(th),
.question-text.exam-mode :deep(h1),
.question-text.exam-mode :deep(h2),
.question-text.exam-mode :deep(h3),
.question-text.exam-mode :deep(h4),
.question-text.exam-mode :deep(h5),
.question-text.exam-mode :deep(h6),
.question-text.exam-mode :deep(code),
.question-text.exam-mode :deep(pre) {
    font-family: "Times New Roman", Times, serif;
}

.question-text.exam-mode :deep(h1),
.question-text.exam-mode :deep(h2),
.question-text.exam-mode :deep(h3),
.question-text.exam-mode :deep(h4),
.question-text.exam-mode :deep(h5),
.question-text.exam-mode :deep(h6) {
    color: #000000;
}
</style>